import { find, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ISearchMatch } from 'src/components/input';
import {
  SORT_DIRECTION,
  SortMenuOption,
  SortOptions,
} from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { PATHS } from 'src/constants/paths';
import { useGetScheduleListWithPagination } from 'src/hooks/api/schedules/scheduleList/useGetScheduleList';
import { useParamQuery } from 'src/hooks/useParamQuery';
import { formatQuery } from 'src/utils';

import { getScheduleItemSelectionKey } from './BulkReplaceComponentPage.helper';
import {
  IBulkReplaceLocationGroup,
  IBulkReplaceScheduleItem,
} from './BulkReplaceComponentPage.interface';

export const useBulkReplaceComponentPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeFilters, getParamsValue, onChangeParams } = useParamQuery({
    sort: ['-created_at'],
    page: ['1'],
    pageSize: ['20'],
  });

  const location = useLocation();
  const [matchedNames, setMatchedNames] = useState<ISearchMatch[]>([]);
  const [isMatchLoading, setIsMatchLoading] = useState<boolean>(false);

  const [selectedScheduleItemsData, setSelectedScheduleItemsData] = useState<
    Map<
      string,
      IBulkReplaceScheduleItem & {
        location_group_id: number;
        location_group_name: string;
        layout_type: string;
      }
    >
  >(new Map());

  const [expandedLocationGroup, setExpandedLocationGroup] = useState<
    number | null
  >(null);
  const [isAllPageSizeSelected, setIsAllPageSizeSelected] = useState(false);

  const selectedScheduleItems = useMemo(
    () => new Set(selectedScheduleItemsData.keys()),
    [selectedScheduleItemsData],
  );

  const selectedLocationGroups = useMemo(() => {
    const locationGroupIds = new Set<number>();
    selectedScheduleItemsData.forEach((item) => {
      locationGroupIds.add(item.location_group_id);
    });

    return locationGroupIds;
  }, [selectedScheduleItemsData]);

  const selectedLocationGroupsData = useMemo(() => {
    if (selectedScheduleItemsData.size === 0) return [];

    const groupMap = new Map<number, IBulkReplaceLocationGroup>();

    selectedScheduleItemsData.forEach((item) => {
      if (!groupMap.has(item.location_group_id)) {
        groupMap.set(item.location_group_id, {
          location_group_id: item.location_group_id,
          location_group_name: item.location_group_name,
          layout_type: item.layout_type as any,
          scheduleItems: [],
        });
      }

      const group = groupMap.get(item.location_group_id)!;
      group.scheduleItems.push({
        scheduleId: item.scheduleId,
        id: item.id,
        scheduleName: item.scheduleName,
        component_id: item.component_id,
        start_date: item.start_date,
        end_date: item.end_date,
        daypart_id: item.daypart_id,
        daypart_name: item.daypart_name,
        position: item.position,
        has_overrides: item.has_overrides,
        modified_at: item.modified_at,
      });
    });

    return Array.from(groupMap.values());
  }, [selectedScheduleItemsData]);

  const mapGetScheduleListParams = (urlParams: string) => {
    let params = urlParams;
    const paramsMap = new Map([
      ['name.list', 'location_group.name'],
      ['name.regex', 'location_group.regex'],
      ['componentId', 'component_id'],
      ['sort=created_at', 'sort=created_at'],
      ['sort=-created_at', 'sort=-created_at'],
      ['sort=modified_at', 'sort=modified_at'],
      ['sort=-modified_at', 'sort=-modified_at'],
      ['sort=name', 'sort=name'],
      ['sort=-name', 'sort=-name'],
    ]);

    paramsMap.forEach((replacement, key) => {
      params = params.replace(key, replacement);
    });

    return formatQuery(params).filter(
      (item) => !item.includes('replaceComponentId'),
    );
  };

  const {
    data: scheduleList,
    isLoading,
    isFetched,
  } = useGetScheduleListWithPagination(
    mapGetScheduleListParams(location.search),
  );

  const rowsPerPage = Number(getParamsValue('pageSize')?.[0]) || 20;
  const currentPage = Number(getParamsValue('page')?.[0]) || 1;

  useEffect(() => {
    if (
      isAllPageSizeSelected &&
      isFetched &&
      scheduleList &&
      scheduleList?.totalCount > 0 &&
      scheduleList?.totalCount !== rowsPerPage
    ) {
      onChangeParams('pageSize', scheduleList.totalCount.toString());
    }
  }, [
    isAllPageSizeSelected,
    onChangeParams,
    rowsPerPage,
    isFetched,
    scheduleList,
  ]);

  const selectedSortOption: SortMenuOption = useMemo(
    () => ({
      option:
        (getParamsValue('sort')?.[0]?.replace('-', '') as SortOptions) ||
        SortOptions.DATE_CREATED,
      order: getParamsValue('sort')?.[0]?.includes('-')
        ? SORT_DIRECTION.DESC
        : SORT_DIRECTION.ASC,
    }),
    [getParamsValue],
  );

  const isAllSelected = scheduleList?.data
    ? Array.from(
        new Set(
          scheduleList.data.map(
            (schedule: IBulkReplaceLocationGroup) => schedule.location_group_id,
          ),
        ),
      ).every((locationGroupId: number) =>
        selectedLocationGroups.has(locationGroupId as number),
      )
    : false;

  const handleCancel = useCallback(() => {
    navigate(PATHS.COMPONENTS);
  }, [navigate]);

  const handleSelectAll = useCallback(() => {
    if (isEmpty(scheduleList?.data)) return;

    const pageItemIds = scheduleList!.data.flatMap(
      (scheduleGroup: IBulkReplaceLocationGroup) =>
        (scheduleGroup.scheduleItems || []).map(
          (item: IBulkReplaceScheduleItem) =>
            getScheduleItemSelectionKey(
              scheduleGroup.location_group_id,
              item.id,
            ),
        ),
    );

    if (isAllSelected) {
      setSelectedScheduleItemsData((prev) => {
        const next = new Map(prev);
        pageItemIds.forEach((itemKey) => next.delete(itemKey));

        return next;
      });
    } else {
      setSelectedScheduleItemsData((prev) => {
        const next = new Map(prev);
        scheduleList!.data.forEach((group: IBulkReplaceLocationGroup) => {
          (group.scheduleItems || []).forEach(
            (item: IBulkReplaceScheduleItem) => {
              next.set(
                getScheduleItemSelectionKey(group.location_group_id, item.id),
                {
                  ...item,
                  location_group_id: group.location_group_id,
                  location_group_name: group.location_group_name,
                  layout_type: group.layout_type,
                },
              );
            },
          );
        });

        return next;
      });
    }
  }, [scheduleList, isAllSelected]);

  const handleClearSelection = useCallback(() => {
    setSelectedScheduleItemsData(new Map());
  }, []);

  const handleToggleExpansion = useCallback((locationGroupId: number) => {
    setExpandedLocationGroup((prev) =>
      prev === locationGroupId ? null : locationGroupId,
    );
  }, []);

  const handleRowsPerPageChange = useCallback(
    (newRowsPerPage: number) => {
      const newSearchParams = new URLSearchParams(searchParams);

      if (newRowsPerPage !== 20) {
        newSearchParams.set('pageSize', newRowsPerPage.toString());
        if (newRowsPerPage === scheduleList?.totalCount) {
          setIsAllPageSizeSelected(true);
        } else {
          setIsAllPageSizeSelected(false);
        }
      } else {
        newSearchParams.delete('pageSize');
        setIsAllPageSizeSelected(false);
      }

      newSearchParams.delete('page');

      setSearchParams(newSearchParams, { replace: true });
    },
    [scheduleList?.totalCount, searchParams, setSearchParams],
  );

  const handlePageChange = useCallback(
    (event: React.ChangeEvent<unknown>, value: number) => {
      if (value !== 1) {
        onChangeParams('page', [value.toString()]);
      } else {
        onChangeParams('page', []);
      }
    },
    [onChangeParams],
  );

  const handleSortChange = useCallback(
    (sortOption: SortMenuOption) => {
      const sortValue = `${
        sortOption.order === SORT_DIRECTION.DESC ? '-' : ''
      }${sortOption.option}`;
      onChangeParams('sort', [sortValue]);
      onChangeParams('page', []);
    },
    [onChangeParams],
  );

  const handleSelectLocationGroup = useCallback(
    (locationGroupId: number) => {
      const locationGroup = find(scheduleList?.data, {
        location_group_id: locationGroupId,
      });

      if (!locationGroup || !locationGroup.scheduleItems) return;

      const groupItemKeys = locationGroup.scheduleItems.map(
        (item: IBulkReplaceScheduleItem) =>
          getScheduleItemSelectionKey(locationGroup.location_group_id, item.id),
      );

      setSelectedScheduleItemsData((prev) => {
        const next = new Map(prev);

        const allSelected = groupItemKeys.every((itemKey) => prev.has(itemKey));

        if (allSelected) {
          groupItemKeys.forEach((itemKey) => next.delete(itemKey));
        } else {
          locationGroup.scheduleItems.forEach(
            (item: IBulkReplaceScheduleItem) => {
              next.set(
                getScheduleItemSelectionKey(
                  locationGroup.location_group_id,
                  item.id,
                ),
                {
                  ...item,
                  location_group_id: locationGroup.location_group_id,
                  location_group_name: locationGroup.location_group_name,
                  layout_type: locationGroup.layout_type,
                },
              );
            },
          );
        }

        return next;
      });
    },
    [scheduleList],
  );

  const handleSelectScheduleItem = useCallback(
    (locationGroupId: number, scheduleItemId: number) => {
      const locationGroup = find(scheduleList?.data, {
        location_group_id: locationGroupId,
      });

      if (!locationGroup || !locationGroup.scheduleItems) return;

      const scheduleItem = find(locationGroup.scheduleItems, {
        id: scheduleItemId,
      });

      if (!scheduleItem) return;

      const itemKey = getScheduleItemSelectionKey(
        locationGroup.location_group_id,
        scheduleItemId,
      );

      setSelectedScheduleItemsData((prev) => {
        const next = new Map(prev);

        if (next.has(itemKey)) {
          next.delete(itemKey);
        } else {
          next.set(itemKey, {
            ...scheduleItem,
            location_group_id: locationGroup.location_group_id,
            location_group_name: locationGroup.location_group_name,
            layout_type: locationGroup.layout_type,
          });
        }

        return next;
      });
    },
    [scheduleList],
  );

  return {
    activeFilters,
    getParamsValue,
    onChangeParams,
    scheduleList,
    isLoading,
    rowsPerPage,
    currentPage,
    selectedSortOption,
    isAllSelected,
    selectedLocationGroups,
    selectedScheduleItems,
    selectedLocationGroupsData,
    expandedLocationGroup,
    handleCancel,
    handleSelectAll,
    handleClearSelection,
    handleToggleExpansion,
    handleRowsPerPageChange,
    handlePageChange,
    handleSortChange,
    handleSelectLocationGroup,
    handleSelectScheduleItem,
    matchedNames,
    setMatchedNames,
    isMatchLoading,
    setIsMatchLoading,
  };
};
